import { useState, useMemo } from "react";
import { useParams, Outlet } from "react-router-dom";

import { useProjectDetail } from "@/hooks/use-project-detail";
import { useUsers, buildUserMap } from "@/hooks/use-users";
import { usePermissions } from "@/hooks/use-permissions";
import { useProjectPermissions } from "@/hooks/use-project-permission";
import { useProjectMutations } from "@/hooks/use-project-mutations";
import { useRecurringAgreementMutations } from "@/hooks/use-recurring-agreements";
import { useProjectProgress } from "@/hooks/use-project-progress";
import { useRecentCompletedTasks } from "@/hooks/use-recent-completed-tasks";
import { useProjectClosingValidation } from "@/hooks/use-project-closing";
import type { ChecklistItem } from "@/lib/db-types";
import { calcLiveBfEstimated, isTaskLine } from "@/lib/billing-calculations";

import { ProjectHeader } from "@/components/project/ProjectHeader";
import { KpiDashboard } from "@/components/project/KpiDashboard";
import { ProjectSidebar } from "@/components/project/ProjectSidebar";
import { ProjectDialogs } from "@/components/project/ProjectDialogs";
import { DEFAULT_CLOSING_CHECKLIST } from "@/components/project/ClosingChecklistDialog";

import { ProjectDetailProvider, type ProjectDetailContextValue } from "@/contexts/ProjectDetailContext";
import { ProjectDetailTabNav } from "./ProjectDetailTabNav";
import { SrIcon } from "@/components/ui/SrIcon";

/**
 * Layout for all project detail routes.
 * Provides shared data via ProjectDetailContext; each tab route renders into <Outlet />.
 */
export default function ProjectDetailLayout() {
  const { id } = useParams();
  const { data, isLoading, error } = useProjectDetail(id);
  const { data: allUsers } = useUsers();
  const { isAdmin, isSuperadmin, activeUserId } = usePermissions();
  const mutations = useProjectMutations(id);
  const { createStorageAgreement } = useRecurringAgreementMutations();
  const { data: progressMap } = useProjectProgress();
  const { data: recentTasksMap } = useRecentCompletedTasks();
  // VIB-722b: pre-beregn full permission-map. Komponenter konsumerer fra
  // context i stedet for å kalle useProjectPermission per komponent.
  const permissions = useProjectPermissions(id);

  // Dialog state
  const [showClosingDialog, setShowClosingDialog] = useState(false);
  const [showReopenDialog, setShowReopenDialog] = useState(false);
  const [showStartupDialog, setShowStartupDialog] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [showDebriefDialog, setShowDebriefDialog] = useState(false);
  const [showActivateDialog, setShowActivateDialog] = useState(false);
  const [closingChecklist, setClosingChecklist] = useState<ChecklistItem[]>([]);

  const userMap = useMemo(() => buildUserMap(allUsers ?? []), [allUsers]);

  const bfCalcPreload = useMemo(
    () => (data ? calcLiveBfEstimated(data.budgetLines, data.offerMetadata) : null),
    [data],
  );

  const closingValidation = useProjectClosingValidation({
    project: data?.project,
    budgetLines: data?.budgetLines,
    vouchers: data?.vouchers,
    invoices: data?.invoices,
    bfEstimated: bfCalcPreload?.liveBfEstimated,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <SrIcon name="loader2" className="w-5 h-5 animate-spin text-muted-foreground"  />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-6 py-8">
        <p className="text-destructive">Feil: {error?.message ?? "Prosjekt ikke funnet"}</p>
      </div>
    );
  }

  const { project: p, customer, owners, invoices, vouchers, voucherAllocations, timeEntries, budgetLines, offerMetadata } = data;
  const internalReadOnly = (p.is_internal && !isAdmin) || p.project_status === "completed";
  const isProjectOwner = owners.some((o) => o.user_id === activeUserId);

  const effectiveProductionLeadId = p.production_lead_id || null;
  const isProductionLead = effectiveProductionLeadId === activeUserId;
  // VIB-1168: Timetildeling-fanens synlighet er nå matrise-styrt via «Budsjett»-
  // rettigheten (view_budget), ikke hardkodet rolle-sjekk. Gjør det
  // konfigurerbart i Tilganger hvem som ser timetildelinger; admin/superadmin
  // overstyrer, PL beholder via edit_budget→view_budget-implikasjon.
  const canSeeBudget = permissions.view_budget;

  // KPI calculations
  const totalTimeSec = timeEntries.reduce((s, t) => s + (t.duration_seconds ?? 0), 0);
  const totalHours = Math.round((totalTimeSec / 3600) * 10) / 10;
  const invoicedAmount = invoices.reduce((s, i) => s + Number(i.amount_excl_vat ?? i.amount ?? 0), 0);
  // Kun interne oppgaver teller som "totale timer i prosjektet". Eksterne
  // kostnadslinjer kan ha quantity_hours satt (typisk innleid utstyr med
  // utleieperiode), men de er ikke arbeid Sølvrev har solgt og skal ikke
  // inn i denne summen — ellers blir Est. timepris og Budsjetteringsavvik
  // feil. Bruker samme isTaskLine-predicate som Timetildeling-pillen.
  const totalBudgetedHours = budgetLines
    .filter(isTaskLine)
    .reduce((s, l) => s + (l.quantity_hours ?? 0), 0);

  const bfCalc = bfCalcPreload ?? calcLiveBfEstimated(budgetLines, offerMetadata);
  const { liveBfEstimated } = bfCalc;
  const bfActual = p.bf_actual != null ? Number(p.bf_actual) : null;

  // Est. timepris = BF estimert / solgte timer. Reflekterer hvor mye
  // dekningsbidrag hver solgt time skal gi når prosjektet leveres på
  // budsjett.
  const estTimepris = totalBudgetedHours > 0 && liveBfEstimated > 0
    ? Math.round(liveBfEstimated / totalBudgetedHours)
    : null;
  // Live timepris = BF reell / brukte timer. Reflekterer faktisk
  // dekningsbidrag per brukt time. Vises som "—" når BF reell ikke er
  // beregnet ennå eller ingen timer er logget.
  const reellTimespris = totalHours > 0 && bfActual != null && bfActual > 0
    ? Math.round(bfActual / totalHours)
    : null;

  const handleStatusChange = (newStatus: string) => {
    if (newStatus === "completed") {
      // VIB-722c: close_project gates oppstart av lukkings-dialogen.
      // Admin/superadmin overstyrer.
      if (!permissions.close_project) {
        return;
      }
      const existing = p.closing_checklist ?? [];
      setClosingChecklist(existing.length > 0 ? existing : DEFAULT_CLOSING_CHECKLIST);
      setShowClosingDialog(true);
      return;
    }
    if (newStatus === "active" && p.project_status !== "active") {
      setShowActivateDialog(true);
      return;
    }
    mutations.updateProjectStatus(newStatus);
  };

  const handleConfirmActivate = async (createInvoice: boolean) => {
    setShowActivateDialog(false);
    await mutations.confirmActivate();
    if (createInvoice) {
      // Invoice creation is handled by the Faktura tab via context
      // Trigger via URL param could be added later
    }
  };

  const handleConfirmClose = async (
    storage?: { storage_gb: number; storage_location: string | null },
  ) => {
    await mutations.confirmClose(closingChecklist);
    // VIB-1308: opprett storage-avtale ved lukking når GB er fanget og
    // prosjektet har kunde. Feiler dette skal ikke lukkingen rulles tilbake —
    // mutationen toaster egen feil, prosjektet er allerede lukket.
    if (storage && p.customer_id) {
      await createStorageAgreement.mutateAsync({
        customer_id: p.customer_id,
        project_id: p.id,
        storage_gb: storage.storage_gb,
        storage_location: storage.storage_location,
        start_date: new Date().toISOString().split("T")[0],
      }).catch(() => { /* toast håndtert i mutation; lukkingen står */ });
    }
    setShowClosingDialog(false);
  };

  const handleConfirmReopen = async () => {
    await mutations.reopen();
  };

  const contextValue: ProjectDetailContextValue = {
    project: p,
    customer,
    owners,
    invoices,
    vouchers,
    voucherAllocations,
    timeEntries,
    budgetLines,
    offerMetadata,
    allUsers: allUsers ?? [],
    userMap,
    isAdmin,
    isProductionLead,
    isProjectOwner,
    canSeeBudget,
    canAssignHours: permissions.edit_budget,
    internalReadOnly,
    effectiveProductionLeadId,
    activeUserId,
    permissions,
    mutations,
  };

  return (
    <ProjectDetailProvider value={contextValue}>
      <div className="page-container animate-fade-in space-y-8">
        <ProjectHeader
          project={p}
          customer={customer}
          owners={owners}
          invoicedAmount={invoicedAmount}
          invoiceCount={invoices.length}
          budgetLines={budgetLines}
          progress={id ? progressMap?.get(id) : undefined}
          recentTasks={id ? recentTasksMap?.get(id) : undefined}
          internalReadOnly={internalReadOnly}
          isAdmin={isAdmin}
          canCloseProject={permissions.close_project}
          onStatusChange={handleStatusChange}
          onStartupClick={() => setShowStartupDialog(true)}
          onStatusMeetingClick={() => setShowStatusDialog(true)}
          onDebriefClick={() => setShowDebriefDialog(true)}
          onReopenClick={() => setShowReopenDialog(true)}
        />

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-8">
          <div className="xl:col-span-8 space-y-8">
            {!p.is_internal && (
              <KpiDashboard
                totalHours={totalHours}
                totalBudgetedHours={totalBudgetedHours}
                bfEstimated={liveBfEstimated}
                bfActual={bfActual}
                estTimepris={estTimepris}
                reellTimespris={reellTimespris}
                canViewEconomics={permissions.view_economics}
              />
            )}

            <ProjectDetailTabNav />

            <Outlet />
          </div>

          <ProjectSidebar
            project={p}
            owners={owners}
            allUsers={allUsers ?? []}
            budgetLines={budgetLines}
            timeEntries={timeEntries}
            userMap={userMap}
            hideTeamWidget={p.is_internal}
          />
        </div>

        <ProjectDialogs
          showClosingDialog={showClosingDialog}
          onClosingDialogOpenChange={setShowClosingDialog}
          closingChecklist={closingChecklist}
          onClosingChecklistChange={setClosingChecklist}
          onClosingConfirm={handleConfirmClose}
          closingValidation={closingValidation}
          closingAdminOverride={isAdmin || isSuperadmin}
          closingHasCustomer={!!p.customer_id}
          projectId={p.id}
          showActivateDialog={showActivateDialog}
          onActivateDialogOpenChange={setShowActivateDialog}
          startupInvoicePct={p.startup_invoice_pct}
          totalAmount={p.total_amount}
          onActivateConfirm={handleConfirmActivate}
          showStartupDialog={showStartupDialog}
          onStartupDialogOpenChange={setShowStartupDialog}
          showStatusDialog={showStatusDialog}
          onStatusDialogOpenChange={setShowStatusDialog}
          showDebriefDialog={showDebriefDialog}
          onDebriefDialogOpenChange={setShowDebriefDialog}
          showReopenDialog={showReopenDialog}
          onReopenDialogOpenChange={setShowReopenDialog}
          onReopenConfirm={handleConfirmReopen}
          projectName={p.name ?? ""}
        />
      </div>
    </ProjectDetailProvider>
  );
}
